import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches a list of franchisee invoices from the backend
 * @param {object} options - Filter and pagination options
 * @param {string} options.fseCode - Filter by franchisee code
 * @param {number} options.pageSize - Number of invoices per page
 * @param {string} options.status - Filter by invoice status
 * @param {string} options.orderBy - Field to order by (default: 'Date')
 * @param {string} options.orderDirection - 'asc' or 'desc'
 * @param {string} options.startAfterDocId - Cursor for pagination
 * @returns {Promise<object>} - { invoices: [], nextPageDocId: '...' }
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated
 */
export const listFranchiseeInvoices = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    pageSize = 30,
    orderBy = 'Date',
    orderDirection = 'desc',
    fseCode,
    status,
    startAfterDocId,
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/franchisee-invoices/list`;

    const body = { pageSize, orderBy, orderDirection };
    if (fseCode) body.fseCode = fseCode;
    if (status) body.status = status;
    if (startAfterDocId) body.startAfterDocId = startAfterDocId;

    console.log("Fetching franchisee invoices:", { url, body });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to list franchisee invoices');
    }

    return responseData.data; // Expected: { invoices: [], nextPageDocId: '...' }
  } catch (error) {
    console.error("Error listing franchisee invoices:", error);
    throw error;
  }
};

/**
 * Syncs franchisee invoices with Xero
 * Imports new invoices and updates existing ones
 * @returns {Promise<object>} - Sync results with counts: { newInvoices, updatedInvoices, processedFranchisees, totalFranchisees }
 * @throws {Error} - Throws an error if the sync fails or the user is not authenticated
 */
export const syncFranchiseeInvoicesWithXero = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/franchisee-invoices/sync-with-xero`;

    console.log("Syncing franchisee invoices with Xero...");

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to sync franchisee invoices');
    }

    return responseData.data; // Expected: { newInvoices, updatedInvoices, processedFranchisees, totalFranchisees }
  } catch (error) {
    console.error("Error syncing franchisee invoices with Xero:", error);
    throw error;
  }
};
